/**
 * Extract a { headers, rows } table from assistant message content or tool payloads.
 * Used by ChatAgent download (CSV/PDF/DOCX). Pure — no React.
 */

export type ExtractedTable = { headers: string[]; rows: string[][] };

function cleanCell(s: string): string {
  return String(s ?? "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/^"|"$/g, "")
    .trim();
}

function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuote = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQuote && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else inQuote = !inQuote;
    } else if (c === "," && !inQuote) {
      out.push(cur);
      cur = "";
    } else {
      cur += c;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

/**
 * Try every known shape (csv/tsv fence, json, markdown table, lists, tsv, yaml, plain csv).
 * When multiple tables exist, return the one with the most rows.
 */
export function extractTableFromMessage(src: string): ExtractedTable | null {
  if (!src || !src.trim()) return null;

  type Tab = ExtractedTable;
  const candidates: Tab[] = [];

  const csvBlocks = src.matchAll(/```(csv|tsv)\s*\n([\s\S]*?)\n```/gi);
  for (const m of csvBlocks) {
    const sep = m[1].toLowerCase() === "tsv" ? "\t" : ",";
    const lines = m[2].split("\n").filter((l) => l.trim());
    if (lines.length >= 2) {
      const split = (l: string) =>
        sep === "," ? splitCsvLine(l).map(cleanCell) : l.split("\t").map(cleanCell);
      candidates.push({ headers: split(lines[0]), rows: lines.slice(1).map(split) });
    }
  }

  const jsonBlocks = src.matchAll(/```json\s*\n([\s\S]*?)\n```/gi);
  for (const m of jsonBlocks) {
    try {
      const parsed = JSON.parse(m[1]);
      if (
        Array.isArray(parsed) &&
        parsed.length > 0 &&
        parsed.every((r) => r && typeof r === "object" && !Array.isArray(r))
      ) {
        const headerSet = new Set<string>();
        parsed.forEach((r: Record<string, unknown>) =>
          Object.keys(r).forEach((k) => headerSet.add(k)),
        );
        const headers = [...headerSet];
        const rows = parsed.map((r: Record<string, unknown>) =>
          headers.map((h) => cleanCell(String(r[h] ?? ""))),
        );
        candidates.push({ headers, rows });
      }
    } catch {
      /* skip */
    }
  }

  const lines = src.split("\n");
  for (let i = 0; i < lines.length - 1; i++) {
    const headerLine = lines[i];
    const sepLine = lines[i + 1]?.trim() ?? "";
    if (
      headerLine.trim().startsWith("|") &&
      headerLine.includes("|") &&
      /^\|?\s*[-:]+/.test(sepLine) &&
      sepLine.includes("-")
    ) {
      const parseRow = (l: string) =>
        l.trim().replace(/^\||\|$/g, "").split("|").map(cleanCell);
      const headers = parseRow(headerLine);
      const rows: string[][] = [];
      for (let j = i + 2; j < lines.length; j++) {
        if (!lines[j]?.trim().startsWith("|")) break;
        rows.push(parseRow(lines[j]));
      }
      if (rows.length > 0) {
        candidates.push({ headers, rows });
        i = i + 2 + rows.length;
      }
    }
  }

  {
    const bulletRegex = /^\s*[-*•]\s+(.+)$/;
    const bullets = lines.map((l) => l.match(bulletRegex)?.[1]).filter(Boolean) as string[];
    if (bullets.length >= 2) {
      const kv = bullets.map((s) => {
        const m = s.match(/^([^:—–-]+?)\s*[:—–-]\s+(.+)$/);
        return m ? [m[1], m[2]] : null;
      });
      if (kv.every(Boolean) && kv.length >= 2) {
        candidates.push({
          headers: ["Name", "Value"],
          rows: kv.map((p) => [cleanCell(p![0]), cleanCell(p![1])]),
        });
      } else {
        candidates.push({ headers: ["Item"], rows: bullets.map((s) => [cleanCell(s)]) });
      }
    }
  }

  {
    const numRegex = /^\s*\d+[.)]\s+(.+)$/;
    const nums = lines.map((l) => l.match(numRegex)?.[1]).filter(Boolean) as string[];
    if (nums.length >= 2) {
      candidates.push({
        headers: ["#", "Item"],
        rows: nums.map((s, i) => [String(i + 1), cleanCell(s)]),
      });
    }
  }

  {
    const defRegex = /^\s*\*\*([^*]+)\*\*\s*[:—–-]\s*(.+)$/;
    const defs = lines.map((l) => l.match(defRegex)).filter(Boolean) as RegExpMatchArray[];
    if (defs.length >= 3) {
      candidates.push({
        headers: ["Name", "Value"],
        rows: defs.map((m) => [cleanCell(m[1]), cleanCell(m[2])]),
      });
    }
  }

  {
    const tsvLines = lines.filter((l) => l.trim() && l.includes("\t"));
    if (tsvLines.length >= 3) {
      const counts = tsvLines.slice(0, 5).map((l) => (l.match(/\t/g) || []).length);
      if (counts[0] >= 1 && counts.every((c) => c === counts[0])) {
        const split = (l: string) => l.split("\t").map(cleanCell);
        candidates.push({ headers: split(tsvLines[0]), rows: tsvLines.slice(1).map(split) });
      }
    }
  }

  {
    const dataLines = lines.filter(
      (l) => l.trim() && l.includes(",") && !l.startsWith("#") && !l.startsWith("|"),
    );
    if (dataLines.length >= 3) {
      const counts = dataLines.slice(0, 5).map((l) => (l.match(/,/g) || []).length);
      if (counts[0] >= 1 && counts.every((c) => c === counts[0])) {
        candidates.push({
          headers: splitCsvLine(dataLines[0]).map(cleanCell),
          rows: dataLines.slice(1).map((l) => splitCsvLine(l).map(cleanCell)),
        });
      }
    }
  }

  if (candidates.length === 0) return null;
  candidates.sort((a, b) => b.rows.length - a.rows.length);
  return candidates[0];
}
